import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import {
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Row,
  Button,
} from "reactstrap";
import TableContainer from "components/Common/TableContainer";
import Loaders from "components/Common/Loaders";
import AllCommonFilterModal from "pages/AllCommonFilter/all-common-filter";
import AddEditNotificationSettingsModal from "pages/AllCommonModal/NotificationSettingsAddEditModal/add-edit-notification-settings-modal";
import { submitNotificationSettingsFilterData } from "model/NotificationSettingsModel";
import { useNotificationSettingsContext } from "libs/context/NotificationSettingsContext";
import { usePermissionContext } from "libs/context/PermissionContext";

const normalizeYesNoLabel = (value) => {
  if (value === true || value === "true" || value === 1 || value === "1") return "Yes";
  if (value === false || value === "false" || value === 0 || value === "0") return "No";
  return value ?? "";
};

const mapRowsFromApi = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((item = {}) => ({
    ...item,
    id: item?.id ?? item?._id ?? item?.notificationSettingsId ?? "",
    toCandidate: normalizeYesNoLabel(item?.toCandidate),
    toClient: normalizeYesNoLabel(item?.toClient),
    toCaller: normalizeYesNoLabel(item?.toCaller),
    toTeamLead: normalizeYesNoLabel(item?.toTeamLead),
    toAccountExecutive: normalizeYesNoLabel(item?.toAccountExecutive),
    toManager: normalizeYesNoLabel(item?.toManager),
    toSuperAdmin: normalizeYesNoLabel(item?.toSuperAdmin),
  }));

const NotificationSettings = () => {
  const { permissionData } = usePermissionContext() || {};
  const {
    fetchNotificationSettingsContextData,
    notificationSettingsContextData,
  } = useNotificationSettingsContext() || {};

  const [searchQuery, setSearchQuery] = useState("");
  const [notificationSettingsData, setNotificationSettingsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableMetaData, setTableMetadata] = useState([]);
  const [commonFilterModalOpen, setCommonFilterModalOpen] = useState(false);
  const [notificationSettingsAddEditModalOpen, setNotificationSettingsAddEditModalOpen] =
    useState(false);
  const [itemId, setItemId] = useState(null);

  const filteredData = useMemo(() => notificationSettingsData, [notificationSettingsData]);

  const notificationSettingsActionPermissions = useMemo(
    () => ({
      canCreate: Boolean(permissionData?.NotificationSettingAccess),
      canEdit: Boolean(permissionData?.NotificationSettingAccess),
    }),
    [permissionData]
  );

  const hasAnyNotificationSettingsRowAction = useMemo(
    () => notificationSettingsActionPermissions.canEdit,
    [notificationSettingsActionPermissions]
  );

  useEffect(() => {
    fetchNotificationSettingsContextData?.();
    getNotificationSettingsList();
  }, []);

  useEffect(() => {
    const columns = notificationSettingsContextData?.table?.columns?.map((col) => {
      const field = String(col?.field ?? "").trim();
      const normalizedField = field.replace(/_/g, "").toLowerCase();
      const isStatusColumn =
        normalizedField === "status" ||
        normalizedField === "settingsstatusid" ||
        String(col?.type ?? "").trim().toLowerCase() === "status";

      return {
        Header: col?.title,
        accessor: isStatusColumn ? "settingsStatusId" : field,
        type: col?.type,
      };
    });
    setTableMetadata(columns);
  }, [notificationSettingsContextData]);

  const getNotificationSettingsList = async (payload = {}) => {
    setLoading(true);
    try {
      const response = await submitNotificationSettingsFilterData(payload);
      const responseData = response?.data || {};
      const listData = responseData?.records ?? responseData?.data ?? responseData ?? [];
      const list = Array.isArray(listData) ? listData : [];

      if (response) {
        setNotificationSettingsData(mapRowsFromApi(list));
      } else {
        setNotificationSettingsData([]);
      }
    } catch (error) {
      setNotificationSettingsData([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterSubmit = async (data) => {
    await getNotificationSettingsList(data);
    setCommonFilterModalOpen(false);
  };

  const handleSearchSubmit = async (value) => {
    await getNotificationSettingsList({
      search: value?.trim() || "",
      nameContains: value?.trim() || "",
    });
  };

  const handleEdit = (row) => {
    const id = row?.id ?? row?._id ?? row?.notificationSettingsId;
    if (!id) return;
    setItemId(id);
    setNotificationSettingsAddEditModalOpen(true);
  };

  const handleCreate = () => {
    setItemId(null);
    setNotificationSettingsAddEditModalOpen(true);
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                <h5 className="fw-bold mb-0">Notification Settings</h5>
                {notificationSettingsActionPermissions.canCreate ? (
                  <Col xs="12" md="4" className="text-md-end p-0">
                    <Button color="primary" onClick={handleCreate}>
                      + New Settings
                    </Button>
                  </Col>
                ) : null}
              </div>
              <hr />

              <Row className="align-items-center g-2 mb-4">
                <Col xs="12" md="8">
                  <div className="d-flex border rounded w-100 category-search-input">
                    <Input
                      type="text"
                      placeholder="Search by name"
                      className="border-0"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event?.target?.value || "")}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          handleSearchSubmit(searchQuery?.trim());
                        }
                      }}
                    />
                    <Button
                      className="search-icon-button"
                      onClick={() => handleSearchSubmit(searchQuery?.trim())}
                    >
                      <i className="fa fa-search" aria-hidden="true" />
                    </Button>
                  </div>
                </Col>
              </Row>

              {!loading && tableMetaData?.length ? (
                <TableContainer
                  columns={tableMetaData}
                  data={filteredData}
                  isActionRequired={hasAnyNotificationSettingsRowAction}
                  isEditAccess={notificationSettingsActionPermissions.canEdit}
                  onEdit={
                    notificationSettingsActionPermissions.canEdit ? handleEdit : undefined
                  }
                  isCheckboxLeftSide={false}
                  isPagination={false}
                  setCommonFilterModalOpen={setCommonFilterModalOpen}
                  pageName="NotificationSettings"
                />
              ) : (
                <Loaders />
              )}
            </CardBody>
          </Card>

          <AllCommonFilterModal
            setCommonFilterModalOpen={setCommonFilterModalOpen}
            commonFilterModalOpen={commonFilterModalOpen}
            modalTitle="Notification Settings"
            handleSubmit={handleFilterSubmit}
            filterData={notificationSettingsContextData?.filters}
          />

          <AddEditNotificationSettingsModal
            notificationSettingsAddEditModalOpen={notificationSettingsAddEditModalOpen}
            setNotificationSettingsAddEditModalOpen={setNotificationSettingsAddEditModalOpen}
            itemId={itemId}
            getNotificationSettingsList={getNotificationSettingsList}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

NotificationSettings.propTypes = {
  orders: PropTypes.array,
  onGetOrders: PropTypes.func,
};

export default withRouter(NotificationSettings);
